import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const segments = [
  {
    slug: 'kitchen',
    label: 'Kitchen',
    description: 'Culinary design samples organized around material, storage, and luminous preparation zones.',
    images: [
      { file: 'Kitchen1.jpeg', caption: 'Clean, sculptural cabinetry and soft stone surfaces.' },
      { file: 'Kitchen2.jpeg', caption: 'Warm finishes with thoughtful task lighting.' },
    //   { file: 'Kitchen3.jpeg', caption: 'Textured details and layered kitchen planning.' },
    ],
  },
  {
    slug: 'living',
    label: 'Living',
    description: 'Entry and circulation studies that prioritize ease, scale, and visual calm.',
    images: [
      { file: 'Hall1.jpeg', caption: 'A gentle sequence of space and materiality.' },
      { file: 'Hall2.jpeg', caption: 'Layered contrast and subtle rhythm in passage.' },
      { file: 'Hall3.jpeg', caption: 'Soft grounding palettes balanced with sculptural form.' },
    ],
  },
  {
    slug: 'tvUnit',
    label: 'TV Unit',
    description: 'Custom-built television units designed to integrate seamlessly with your living space.',
    images: [
      { file: 'TVUnit1.jpeg', caption: 'Sleek lines and hidden storage solutions.' },
      { file: 'TVUnit2.jpeg', caption: 'Warm wood finishes that complement any space.' },
      { file: 'TVUnit3.jpeg', caption: 'Integrated lighting for a cozy ambiance.' },
    ],
  },
  {
    slug: 'bedroom',
    label: 'Bedroom',
    description: 'Intimate spaces designed for comfort and tranquility.',
    images: [
      { file: 'Bedroom1.jpeg', caption: 'Soft textures and gentle lighting create a restful atmosphere.' },
      { file: 'Bedroom2.jpeg', caption: 'Strategic storage solutions that maintain a clean aesthetic.' },
      { file: 'Bedroom3.jpeg', caption: 'A harmonious blend of form and function in intimate quarters.' },
    ],
  },
  {
    slug: 'elevation',
    label: 'Elevation',
    description: 'Architectural elevation studies with clear geometry and refined detail.',
    images: [
      { file: 'Elevation1.jpeg', caption: 'Strong framing and directional composition.' },
      { file: 'Elevation2.jpeg', caption: 'Textural emphasis and elevated detail studies.' },
      { file: 'Elevation3.jpeg', caption: 'A crisp gesture with layered light and shadow.' },
    ],
  },
];

export default function ProjectGallery() {
  const [activeSegment, setActiveSegment] = useState(segments[0].slug);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const selected = segments.find((segment) => segment.slug === activeSegment);
  const currentImage = selected.images[activeImageIndex];

  return (
    <section className="relative overflow-hidden py-16" id = "work">
      <div className="mx-auto max-w-[1400px] px-6">
        <div className="mb-10 max-w-3xl space-y-4">
          <p className="text-sm uppercase tracking-[0.4em] text-amber-200/90">Featured Work</p>
          <h2 className="text-4xl font-semibold leading-tight text-white sm:text-5xl">A segmented gallery for every design moment.</h2>
          <p className="text-base leading-8 text-gray-300">
            Tap each segment to focus the collection, then explore the tiles that showcase the sample images within that story.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 mb-10">
          {segments.map((segment) => {
            const isActive = segment.slug === activeSegment;
            return (
              <motion.button
                key={segment.slug}
                type="button"
                onClick={() => {
                  setActiveSegment(segment.slug);
                  setActiveImageIndex(0);
                }}
                layout
                className={`relative overflow-hidden rounded-3xl border px-5 py-4 text-left transition ${isActive ? 'border-amber-300/60 bg-white/10 text-white shadow-[0_20px_70px_rgba(246,194,109,0.06)]' : 'border-white/10 bg-white/5 text-gray-200 hover:border-amber-300/30 hover:bg-white/10'}`}
              >
                {isActive && (
                  <motion.span
                    layoutId="segmentHighlight"
                    className="absolute inset-0 rounded-3xl bg-amber-200/10"
                    transition={{ type: 'spring', stiffness: 220, damping: 24 }}
                  />
                )}
                <span className="relative z-10 block text-sm uppercase tracking-[0.35em] text-amber-200/90">Segment</span>
                <span className="relative z-10 mt-3 block text-xl font-semibold text-white">{segment.label}</span>
              </motion.button>
            );
          })}
        </div>

        <div className="grid gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
            className="relative overflow-hidden rounded-[36px] border border-white/10 bg-[#111111]/90 shadow-[0_30px_90px_rgba(0,0,0,0.35)]"
          >
            <div className="relative flex min-h-[320px] items-center justify-center overflow-hidden bg-black/10 px-4 py-8 sm:min-h-[420px]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={`${selected.slug}-${currentImage.file}-${activeImageIndex}`}
                  src={`/gallery/segments/${currentImage.file}`}
                  alt={currentImage.caption}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -24 }}
                  transition={{ duration: 0.45, ease: 'easeOut' }}
                  className="max-h-[520px] max-w-full object-contain"
                />
              </AnimatePresence>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-8 text-white">
                <p className="text-sm uppercase font-semibold tracking-[0.35em] text-amber-200/90">{selected.label}</p>
                <h3 className="mt-3 text-3xl leading-tight text-white sm:text-xl">{currentImage.caption}</h3>
                {/* <p className="mt-3 max-w-2xl text-sm text-gray-300">{selected.description}</p> */}
              </div>
              <div className="absolute inset-y-0 left-0 flex items-center">
                <button
                  type="button"
                  disabled={activeImageIndex === 0}
                  onClick={() => setActiveImageIndex((index) => Math.max(index - 1, 0))}
                  className="m-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white transition disabled:cursor-not-allowed disabled:opacity-40 hover:bg-white/10"
                >
                  ‹
                </button>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center justify-end">
                <button
                  type="button"
                  disabled={activeImageIndex === selected.images.length - 1}
                  onClick={() => setActiveImageIndex((index) => Math.min(index + 1, selected.images.length - 1))}
                  className="m-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white transition disabled:cursor-not-allowed disabled:opacity-40 hover:bg-white/10"
                >
                  ›
                </button>
              </div>
            </div>

            <div className="border-t border-white/10 px-8 py-6 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm uppercase tracking-[0.35em] text-amber-200/90">Viewing</p>
                <p className="mt-2 text-xl font-semibold text-white">{selected.label}</p>
                <p className="mt-2 max-w-2xl text-sm text-gray-300">{selected.description}</p>
              </div>
              <div className="mt-4 flex items-center gap-2 sm:mt-0">
                {selected.images.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImageIndex(idx)}
                    className={`h-2.5 w-2.5 rounded-full transition ${idx === activeImageIndex ? 'bg-amber-200' : 'bg-white/30 hover:bg-white/50'}`}
                  />
                ))}
                <span className="ml-4 text-sm text-gray-300">{activeImageIndex + 1} of {selected.images.length}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
